import { intersectionBy } from 'lodash';
import type { JsonData, JsonDataItem } from '../types';
import type { VisibilityConditionValueTypes } from './converters/target-value/i-target-value-converter';
import type { VisibilityCondition } from './visibility-condition.interfaces';

export abstract class VisibilityConditionBase {
  public readonly fieldCondition: VisibilityCondition;

  protected constructor(fieldCondition: VisibilityCondition) {
    this.fieldCondition = fieldCondition;
  }

  public get VisibilityType() {
    return this.fieldCondition.fieldVisibilityType;
  }

  public get defaultIsHidden() {
    return true;
  }

  public get sourceFieldId() {
    return this.fieldCondition.sourceFieldId;
  }

  /**
   * Implemented in the concrete instances; this method will run an individual visibility condition to determine if a
   * field should be shown. If false is returned from this method then that means the field will be shown (isHidden =
   * false).
   * @param {JsonDataItem | JsonData} currentData - The data used to decide if a field should be shown.
   * @returns {boolean} Used to set the isHidden property of a field setting.
   */
  public abstract runVisibilityCondition(
    currentData: JsonDataItem | JsonData,
  ): boolean;

  /**
   * Determines whether a visibility condition should show or hide a form item.
   * @param {VisibilityConditionValueTypes} dataValue - The current forms data value.
   * @param {VisibilityConditionValueTypes} visibilityValue - The visibility conditions value to match.
   * @param {boolean} flipCheck - If true the check changes to a not equals instead of equals.
   * @returns {boolean} Indicates if the form item should be hidden or shown.
   * @protected
   */
  protected determineIsHidden(
    dataValue?:
      | VisibilityConditionValueTypes
      | Array<VisibilityConditionValueTypes>,
    visibilityValue?:
      | VisibilityConditionValueTypes
      | Array<VisibilityConditionValueTypes>,
    flipCheck = false,
  ) {
    // when both of these items are undefined that should not trigger the visibility condition
    if (dataValue === undefined && visibilityValue === undefined) {
      return true;
    }

    if (Array.isArray(visibilityValue) && Array.isArray(dataValue)) {
      // Determines if there are any duplicate values between two arrays by initially converting them into the same string type.
      const isAnyVisibilityValueOnDataArray =
        intersectionBy(visibilityValue, dataValue, String).length > 0;
      return flipCheck
        ? isAnyVisibilityValueOnDataArray
        : !isAnyVisibilityValueOnDataArray;
    } else if (Array.isArray(dataValue) && !Array.isArray(visibilityValue)) {
      const isAnyVisibilityMatch = dataValue.some(
        (item) => item === visibilityValue,
      );
      return flipCheck ? isAnyVisibilityMatch : !isAnyVisibilityMatch;
    } else if (!Array.isArray(visibilityValue)) {
      if (dataValue === visibilityValue && !flipCheck) {
        return false;
      } else if (dataValue !== visibilityValue && flipCheck) {
        return false;
      }
    } else {
      if (
        visibilityValue.findIndex((value) => dataValue === value) > -1 &&
        !flipCheck
      ) {
        return false;
      } else if (
        visibilityValue.findIndex((value) => dataValue === value) === -1 &&
        flipCheck
      ) {
        return false;
      }
    }
    return true;
  }
}
